/**
 * Electronic Invoicing Hook
 *
 * Provides state and actions for electronic invoicing configuration.
 * Fetches status from backend and provides toggle functionality.
 */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import api from '@/lib/api'

// Types
export interface InvoicingConfig {
  enabled: boolean
  has_credentials: boolean
  environment: string
  sandbox_mode: boolean
  connection_status: 'connected' | 'disabled' | 'not_configured' | 'warning'
  last_sync: string | null
  failed_count: number
  last_error: string | null
}

export interface InvoicingStats {
  total_submitted: number
  total_accepted: number
  total_rejected: number
  total_pending: number
  total_errors: number
  success_rate: number
}

export interface PendingDocument {
  id: number
  bill_type: string
  client_name: string | null
  total: number
  created_at: string
  hacienda_status: string | null
  hacienda_message: string | null
}

export interface PendingDocumentsResponse {
  count: number
  documents: PendingDocument[]
}

export interface SubmissionAttempt {
  id: number
  bill_id: number
  attempt_number: number
  status: string
  error_message: string | null
  error_code: string | null
  request_payload: string | null
  response_payload: string | null
  created_at: string
}

export interface SubmissionHistoryResponse {
  bill_id: number
  attempts: SubmissionAttempt[]
}

// ---------------------------------------------------------------------------
// Incidents (controlled error/rejection review process)
// ---------------------------------------------------------------------------

export type IncidentWorkflowStatus = 'open' | 'in_progress' | 'resolved' | 'dismissed'
export type IncidentKind = 'bill' | 'purchase' | 'rep'
export type IncidentTypeValue = 'submission_error' | 'rejected' | 'connection_error'

export interface ParsedError {
  category: string
  code: string | null
  raw: string
  target: string | null
  /** Human-friendly resolution of `target` (e.g. "Línea 13: Gaza Plastica (código 31395)"). */
  target_label?: string | null
}

export interface IncidentSummary {
  id: number
  document_kind: IncidentKind
  document_id: number
  bill_id: number | null
  purchase_id: number | null
  payment_receipt_id: number | null
  incident_type: IncidentTypeValue
  source_status: string | null
  error_code: string | null
  error_message: string | null
  errors: ParsedError[]
  status: IncidentWorkflowStatus
  client_name: string | null
  total: number
  consecutive: string | null
  /** Fecha del documento que fallo. Distinta de last_seen_at, que es cuando el
   *  scan lo reviso por ultima vez y por eso se reescribe en cada pasada. */
  document_date: string | null
  current_hacienda_status: string | null
  assigned_to_name: string | null
  resolved_by_name: string | null
  resolution_notes: string | null
  first_detected_at: string
  last_seen_at: string
  resolved_at: string | null
}

export interface IncidentAttempt {
  id: number
  attempt_number: number
  status: string
  error_code: string | null
  error_message: string | null
  request_payload: string | null
  response_payload: string | null
  created_at: string
}

export interface IncidentDetail extends IncidentSummary {
  current_hacienda_message: string | null
  xml_response: string | null
  pdf_url: string | null
  attempts: IncidentAttempt[]
}

export interface IncidentListResponse {
  items: IncidentSummary[]
  total: number
  page: number
  size: number
}

export interface IncidentStats {
  open: number
  in_progress: number
  resolved: number
  dismissed: number
  active: number
  by_category: Record<string, number>
}

export interface IncidentScanResult {
  created: number
  updated: number
  auto_resolved: number
  active_total: number
  message: string
}

export interface IncidentListParams {
  page?: number
  size?: number
  status?: string
  incident_type?: string
  document_kind?: string
  error_category?: string
  search?: string
  sort?: string
}

export interface IncidentUpdatePayload {
  status?: IncidentWorkflowStatus
  resolution_notes?: string
  assigned_to_user_id?: number | null
}

// Query keys
const QUERY_KEYS = {
  config: ['invoicing', 'config'] as const,
  stats: ['invoicing', 'stats'] as const,
  pending: ['invoicing', 'pending'] as const,
  history: (billId: number) => ['invoicing', 'history', billId] as const,
  incidents: (params: IncidentListParams) => ['invoicing', 'incidents', params] as const,
  incident: (id: number) => ['invoicing', 'incident', id] as const,
  incidentStats: ['invoicing', 'incident-stats'] as const,
}

/**
 * Hook for fetching invoicing configuration
 */
export function useInvoicingConfig() {
  return useQuery({
    queryKey: QUERY_KEYS.config,
    queryFn: async () => {
      const response = await api.get<InvoicingConfig>('/invoicing/config')
      return response.data
    },
    refetchInterval: 30000, // Refresh every 30 seconds
    staleTime: 10000, // Consider stale after 10 seconds
  })
}

/**
 * Hook for fetching invoicing statistics
 */
export function useInvoicingStats() {
  return useQuery({
    queryKey: QUERY_KEYS.stats,
    queryFn: async () => {
      const response = await api.get<InvoicingStats>('/invoicing/stats')
      return response.data
    },
    staleTime: 60000, // Consider stale after 1 minute
  })
}

/**
 * Hook for fetching pending documents
 */
export function usePendingDocuments(limit: number = 50) {
  return useQuery({
    queryKey: QUERY_KEYS.pending,
    queryFn: async () => {
      const response = await api.get<PendingDocumentsResponse>('/invoicing/pending', {
        params: { limit },
      })
      return response.data
    },
    staleTime: 30000,
  })
}

/**
 * Hook for fetching submission history for a bill
 */
export function useSubmissionHistory(billId: number) {
  return useQuery({
    queryKey: QUERY_KEYS.history(billId),
    queryFn: async () => {
      const response = await api.get<SubmissionHistoryResponse>(`/invoicing/history/${billId}`)
      return response.data
    },
    enabled: billId > 0,
  })
}

/**
 * Hook for toggling invoicing enabled/disabled
 */
export function useToggleInvoicing() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await api.post('/invoicing/config/toggle', { enabled })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.config })
    },
  })
}

/**
 * Hook for resetting the failed count
 */
export function useResetFailedCount() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response = await api.post('/invoicing/reset-failed-count')
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.config })
    },
  })
}

/**
 * Hook for initializing configuration
 */
export function useInitConfig() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async () => {
      const response = await api.post('/invoicing/init-config')
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.config })
    },
  })
}

/**
 * Hook for submitting a document to Hacienda
 */
export function useSubmitDocument() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({ billId, withRetry = true }: { billId: number; withRetry?: boolean }) => {
      const response = await api.post(`/invoicing/documents/submit/${billId}`, null, {
        params: { with_retry: withRetry },
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pending })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.stats })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.config })
      // Refresh the incident screens so the new Hacienda status shows at once.
      queryClient.invalidateQueries({ queryKey: ['invoicing', 'incidents'] })
      queryClient.invalidateQueries({ queryKey: ['invoicing', 'incident'] })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.incidentStats })
    },
  })
}

/**
 * Hook for syncing document status from Hacienda
 */
export function useSyncDocumentStatus() {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async (billId: number) => {
      const response = await api.post(`/invoicing/documents/sync/${billId}`)
      return response.data
    },
    onSuccess: (_, billId) => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.history(billId) })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.pending })
      queryClient.invalidateQueries({ queryKey: ['invoicing', 'incidents'] })
      queryClient.invalidateQueries({ queryKey: ['invoicing', 'incident'] })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.incidentStats })
    },
  })
}

/**
 * Hook for listing invoicing incidents (admin)
 */
export function useIncidents(params: IncidentListParams) {
  return useQuery({
    queryKey: QUERY_KEYS.incidents(params),
    queryFn: async () => {
      const query = new URLSearchParams()
      query.set('page', String(params.page ?? 1))
      query.set('size', String(params.size ?? 20))
      if (params.status && params.status !== 'all') query.set('status', params.status)
      if (params.incident_type && params.incident_type !== 'all')
        query.set('incident_type', params.incident_type)
      if (params.document_kind && params.document_kind !== 'all')
        query.set('document_kind', params.document_kind)
      if (params.error_category && params.error_category !== 'all')
        query.set('error_category', params.error_category)
      if (params.search) query.set('search', params.search)
      if (params.sort) query.set('sort', params.sort)
      const response = await api.get<IncidentListResponse>(`/invoicing/incidents?${query}`)
      return response.data
    },
    // Incidents must reflect Hacienda's live state — never serve cached data.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })
}

/**
 * Hook for fetching a single incident's full detail
 */
export function useIncident(id: number | null) {
  return useQuery({
    queryKey: QUERY_KEYS.incident(id ?? 0),
    queryFn: async () => {
      const response = await api.get<IncidentDetail>(`/invoicing/incidents/${id}`)
      return response.data
    },
    enabled: !!id,
    // Always fetch fresh: the detail page is used to work the incident in real
    // time (resend/sync change the Hacienda status), so cached data is wrong.
    staleTime: 0,
    gcTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  })
}

/**
 * Hook for the incident counts by workflow status (for badges/headers).
 * Pass `enabled=false` for non-admins (the endpoint is admin-only).
 */
export function useIncidentStats(enabled: boolean = true) {
  return useQuery({
    queryKey: QUERY_KEYS.incidentStats,
    queryFn: async () => {
      const response = await api.get<IncidentStats>('/invoicing/incidents/stats')
      return response.data
    },
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
    enabled,
  })
}

/**
 * Hook for running the incident scan (detection/backfill)
 */
export function useScanIncidents() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async () => {
      const response = await api.post<IncidentScanResult>('/invoicing/incidents/scan')
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['invoicing', 'incidents'] })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.incidentStats })
    },
  })
}

/**
 * Hook for updating an incident's workflow state / resolution note
 */
export function useUpdateIncident() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, payload }: { id: number; payload: IncidentUpdatePayload }) => {
      const response = await api.patch<IncidentDetail>(`/invoicing/incidents/${id}`, payload)
      return response.data
    },
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['invoicing', 'incidents'] })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.incident(id) })
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.incidentStats })
    },
  })
}

// ---------------------------------------------------------------------------
// Received documents (supplier inbox → Mensaje Receptor acceptance)
// ---------------------------------------------------------------------------

export interface ReceivedDocument {
  document_key: string
  document_type: string
  consecutive: string
  issue_date: string
  issuer_id_type: string
  issuer_id: string
  issuer_name: string
  issuer_email: string | null
  subtotal: number
  total_tax: number
  total: number
  reception_status: string | null
  response_message_key: string | null
  received_at: string
  // false => el proveedor no existe y se creará automáticamente al aceptar
  provider_exists: boolean
  // true => el proveedor existe pero su cédula y/o tipo difieren del XML;
  // al aceptar se actualizan automáticamente (el XML es la fuente de la verdad)
  provider_needs_update: boolean
  provider_current_name: string | null
  provider_current_identification: string | null
  provider_current_type_identification: string | null
  // Receptor del XML vs cédula del tenant. Si no coinciden, Hacienda rechaza el
  // Mensaje Receptor (solo el receptor del documento puede responderlo).
  receiver_id_type: string | null
  receiver_id: string | null
  receiver_name: string | null
  receiver_mismatch: boolean
  tenant_identification: string | null
}

/**
 * Upload + parse a supplier's electronic document XML.
 *
 * Alanube CRI has no reception inbox to poll, so (like the legacy system) the
 * user uploads the XML the supplier sent them; the backend extracts Clave +
 * Emisor + totals and returns them to drive the acceptance/rejection.
 */
export function useParseSupplierXml() {
  return useMutation({
    mutationFn: async (file: File) => {
      const form = new FormData()
      form.append('file', file)
      const response = await api.post<ReceivedDocument>('/invoicing/received/parse', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      })
      return response.data
    },
  })
}

/** Accept (1) or partially accept (2) a parsed supplier document. */
export function useAcceptReceivedDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      document,
      messageType = '1',
      detailMessage,
    }: {
      document: ReceivedDocument
      messageType?: '1' | '2'
      detailMessage?: string
    }) => {
      const response = await api.post('/invoicing/received/accept', {
        document,
        message_type: messageType,
        detail_message: detailMessage,
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchases'] })
    },
  })
}

/** Reject (3) a parsed supplier document. */
export function useRejectReceivedDocument() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      document,
      rejectionReason,
    }: {
      document: ReceivedDocument
      rejectionReason: string
    }) => {
      const response = await api.post('/invoicing/received/reject', {
        document,
        rejection_reason: rejectionReason,
      })
      return response.data
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['purchases'] })
    },
  })
}

/**
 * Utility function to format relative time
 */
export function formatRelativeTime(dateString: string | null): string {
  if (!dateString) return ''

  const date = new Date(dateString)
  const now = new Date()
  const diffMs = now.getTime() - date.getTime()
  const diffMins = Math.floor(diffMs / 60000)
  const diffHours = Math.floor(diffMs / 3600000)
  const diffDays = Math.floor(diffMs / 86400000)

  if (diffMins < 1) return 'Hace un momento'
  if (diffMins < 60) return `Hace ${diffMins} minutos`
  if (diffHours < 24) return `Hace ${diffHours} horas`
  if (diffDays < 7) return `Hace ${diffDays} días`

  return date.toLocaleDateString('es-CR')
}
